<script setup lang="ts">
import { computed, ref } from 'vue'
import type { BuffettChecklistResult } from '@/api/client'

const props = defineProps<{
  buffettChecklist: BuffettChecklistResult
}>()

const GATE_EMOJI: Record<string, string> = {
  circle_of_competence: '🎯',
  economic_moat: '💎',
  moat_durability: '🏰',
  management_allocation: '👔',
  financial_health: '🛡️',
  margin_of_safety: '📐',
}

const VERDICT_META: Record<string, { label: string; type: 'success' | 'warning' | 'danger' }> = {
  pass: { label: '通过', type: 'success' },
  grey_zone: { label: '灰色地带', type: 'warning' },
  reject: { label: '一票否决', type: 'danger' },
}

const verdictMeta = computed(() => VERDICT_META[props.buffettChecklist.verdict] || { label: props.buffettChecklist.verdict, type: 'warning' as const })

const gateList = computed(() => Object.values(props.buffettChecklist.gates || {}))

const activeGates = ref<string[]>(['circle_of_competence'])

const scoreColor = computed(() => {
  const s = props.buffettChecklist.score ?? 0
  if (s >= 75) return 'var(--success)'
  if (s >= 50) return 'var(--warning)'
  return 'var(--danger)'
})
</script>

<template>
  <el-card shadow="never" class="buffett-panel">
    <template #header>
      <div class="header-bar">
        <span class="title">📋 巴菲特买入前六关 Checklist (Buffett 6-Gate)</span>
        <div class="header-tags">
          <el-tag :type="verdictMeta.type" effect="dark" size="small">总评: {{ verdictMeta.label }}</el-tag>
          <el-tag type="info" effect="plain" size="small">
            通过 {{ buffettChecklist.passed_gates_count }}/{{ buffettChecklist.total_gates }} 关
          </el-tag>
        </div>
      </div>
    </template>

    <!-- 总分条 -->
    <div class="score-bar">
      <span class="score-label">总分</span>
      <strong class="score-value" :style="{ color: scoreColor }">{{ buffettChecklist.score ?? '-' }}</strong>
      <span class="score-total">/ 100</span>
      <span class="summary">{{ buffettChecklist.summary }}</span>
    </div>

    <!-- 红线一票否决 -->
    <el-alert
      v-for="(flag, i) in buffettChecklist.red_flags || []"
      :key="i"
      :title="`🚫 红线一票否决: ${flag}`"
      type="error"
      :closable="false"
      show-icon
      class="red-flag"
    />

    <!-- 六道大关折叠卡 -->
    <el-collapse v-model="activeGates">
      <el-collapse-item
        v-for="gate in gateList"
        :key="gate.gate_key"
        :name="gate.gate_key"
      >
        <template #title>
          <div class="gate-title">
            <span class="gate-name">{{ GATE_EMOJI[gate.gate_key] || '🔘' }} {{ gate.gate_name }}</span>
            <span class="gate-score" :style="{ color: gate.passed ? 'var(--success)' : 'var(--danger)' }">
              {{ gate.score }}/{{ gate.weight }} {{ gate.passed ? '✅' : '❌' }}
            </span>
          </div>
        </template>
        <div v-for="(check, i) in gate.checks || []" :key="i" class="gate-check">
          <span>{{ check.passed ? '✅' : '❌' }}</span>
          <span class="check-name">{{ check.name }}</span>
          <span class="check-detail">— {{ check.detail }}</span>
        </div>
      </el-collapse-item>
    </el-collapse>
  </el-card>
</template>

<style scoped>
.buffett-panel { display: flex; flex-direction: column; gap: 10px; }
.header-bar { display: flex; justify-content: space-between; align-items: center; width: 100%; }
.header-tags { display: flex; gap: 6px; }
.title { font-size: 15px; font-weight: 600; }
.score-bar {
  display: flex; align-items: baseline; gap: 6px; font-size: 13px;
  color: var(--text-secondary);
}
.score-value { font-size: 22px; font-weight: 800; }
.score-total { color: var(--text-muted); }
.summary { margin-left: 10px; line-height: 1.5; }
.red-flag { margin-bottom: 4px; }
.gate-title { display: flex; justify-content: space-between; width: 100%; padding-right: 10px; }
.gate-name { font-size: 14px; font-weight: 600; color: var(--text-primary); }
.gate-score { font-size: 13px; font-weight: 700; }
.gate-check {
  display: flex; gap: 6px; padding: 4px 0; font-size: 13px;
  color: var(--text-primary); line-height: 1.5;
}
.check-name { font-weight: 600; }
.check-detail { color: var(--text-secondary); }
</style>
